import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { isEmptyAdf } from '@confluence/comments-util/entry-points/domUtils.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import {
	expVal,
	isExperimentEnabled,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { LoadableLazy } from '@confluence/loadable';
import {
	type ObjectSidebarRemixLifecycleAdapter,
	useObjectSidebarRemixLifecycle,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import { useObjectSidebarRemixLifecycleDiagnostics } from '@confluence/malleable-ui-common/entry-points/useObjectSidebarRemixLifecycleDiagnostics.ts';
import type { PageRemixCloseOptions } from '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts';
import { prepareObjectSidebarPageRemixStart } from '@confluence/malleable-ui-common/entry-points/ObjectSidebarPageRemixStart.ts';
import { useSpaceRemixOptionsPrefetch } from '@confluence/malleable-ui-common/entry-points/useShouldHideDiagramRemixOption.ts';
import { subscribeToObjectSidebarRemixPickerClose } from '@confluence/malleable-ui-common/entry-points/objectSidebarRemixPickerClose.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { useObjectSidebarRemixLifecycleSession } from './ObjectSidebarRemixLifecycleSession.tsx';
import { parseObjectSidebarRemixAdf } from './objectSidebarRemixAdf.ts';

export const OBJECT_LEVEL_REMIX_ENTRY_POINT = 'objectSidebar';
export const OBJECT_LEVEL_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';

const remixObjectPagePopupLoader = () =>
	import(/* webpackChunkName: "loadable-RemixObjectPagePopup" */ './RemixObjectPagePopup.tsx');

const RemixObjectPagePopup = LoadableLazy({
	loader: async () => (await remixObjectPagePopupLoader()).RemixObjectPagePopup,
});

const remixButtonContainerStyle = css({
	position: 'relative',
});

const popupTriggerOverlayStyle = css({
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
});

const placeholderButtonStyle = css({
	visibility: 'hidden',
});

const i18n = defineMessages({
	remixPageLabel: {
		id: 'object-sidebar-components.remix-page.label',
		defaultMessage: 'Remix page',
		description: 'Label for the button that opens the page-level remix overlay',
	},
	remixPageTooltip: {
		id: 'object-sidebar-components.remix-page.tooltip',
		defaultMessage: 'Remix',
		description: 'Tooltip for the button that opens the object-level remix overlay',
	},
	remixPageEmptyTooltip: {
		id: 'object-sidebar-components.remix-page.empty-tooltip',
		defaultMessage: 'Add content to this page to remix',
		description: 'Tooltip shown when the remix button is disabled because the page has no content',
	},
	remixInProgressTooltip: {
		id: 'object-sidebar-components.remix-page.in-progress-tooltip',
		defaultMessage: 'Remix in progress',
		description:
			'Tooltip shown when the remix button is disabled because a remix is already in progress',
	},
});

type RemixPanelButtonProps = {
	contentId?: string;
	contentBodyValue?: string | null;
	getAdfDocument?: () => Promise<DocNode | null>;
	isEditorEmpty?: boolean;
	isDisabled?: boolean;
	lifecycleAdapter?: ObjectSidebarRemixLifecycleAdapter;
};

export const RemixPanelButton = ({
	contentId,
	contentBodyValue,
	getAdfDocument,
	isEditorEmpty,
	isDisabled = false,
}: RemixPanelButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { didPrefetch: didPrefetchSpaceOptions, hiddenOptions: prefetchedSpaceHiddenOptions } =
		useSpaceRemixOptionsPrefetch({
			// The modal records the permissions exposure when opened; prefetching must not record it.
			// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
			enabled: UNSAFE_expValEqualsNoExposure(
				'cc_maui_remix_attachment_permissions',
				'isEnabled',
				true,
			),
		});
	const { showSmartCreationModal } = useSmartCreationModalState();
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const experienceTracker = useAIMateExperienceTracker();
	const { openConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();
	const {
		admitObjectSidebarRemixLifecycle,
		closeObjectSidebarRemixModal,
		hasAdapter: shouldAttemptLifecycleAdmission,
		isObjectSidebarRemixModalOpen,
		isRemixStartBlockedByActiveFlow,
		openObjectSidebarRemixModal,
		registerPopupAnchor,
		releaseRetainedLifecycleForToken,
		retainedLifecycleToken,
	} = useObjectSidebarRemixLifecycleSession();
	// `handleOpenSidebarConversation` is invoked asynchronously from the remix flow
	// (after `sendAIFCPrompt` resolves), by which point the sidebar's active state may
	// have changed. Track the latest value in a ref so the guard reads the current
	// value at invocation time rather than a stale value captured at render time.
	// Mirrors the existing async-safe ref pattern (e.g. `isRemixCancelled`).
	const isConversationAssistantSidebarActiveRef = useRef(isConversationAssistantSidebarActive);
	isConversationAssistantSidebarActiveRef.current = isConversationAssistantSidebarActive;

	const handleOpenSidebarConversation = useCallback(
		(conversationId: string) => {
			updatePageRovoParams({ pathway: 'chat', conversationId });
			if (!isConversationAssistantSidebarActiveRef.current) {
				openConversationAssistant('sidebar');
			}
		},
		[openConversationAssistant],
	);

	const isRemixStartActive = useRef(false);
	const hasOpenedRemixModalRef = useRef(false);
	const startRequestIdRef = useRef(0);
	const adfSourceVersionRef = useRef(0);
	const lifecycleAdfSourceRef = useRef({ contentId, contentBodyValue, getAdfDocument });
	const isLifecycleTreatmentActiveRef = useRef(false);
	useEffect(() => {
		if (!isLifecycleTreatmentActiveRef.current) {
			return;
		}
		lifecycleAdfSourceRef.current = { contentId, contentBodyValue, getAdfDocument };
		adfSourceVersionRef.current += 1;
	}, [contentId, contentBodyValue, getAdfDocument]);
	useEffect(
		() => () => {
			if (!isLifecycleTreatmentActiveRef.current) {
				return;
			}
			adfSourceVersionRef.current += 1;
			startRequestIdRef.current += 1;
		},
		[],
	);

	const [isModalOpen, setIsModalOpen] = useState(false);
	// With this sidebar's own popup open, the button is that popup's close toggle (a dismiss control),
	// so another active flow only blocks new starts while the popup is closed. Once this sidebar's own
	// flow is admitted, the popup has given way to the thinking bar, so the button is no longer its
	// close toggle even while the popup session is still open (a deferred custom prompt keeps it open
	// until the reply is ready).
	const { isAdmitted: isOwnFlowAdmitted } = useObjectSidebarRemixLifecycle();
	const isNewStartBlocked =
		isRemixStartBlockedByActiveFlow &&
		(!(isModalOpen || isObjectSidebarRemixModalOpen) || isOwnFlowAdmitted);
	const [lazyAdfDocument, setLazyAdfDocument] = useState<DocNode | null>(null);
	const [viewOpenAdfDocument, setViewOpenAdfDocument] = useState<DocNode | null>(null);

	const hasContentFromBody = useMemo(() => {
		if (getAdfDocument) {
			return false;
		}
		if (!contentBodyValue) {
			return false;
		}
		const parsed = parseObjectSidebarRemixAdf(contentBodyValue, contentId, { readOnly: true });
		return parsed !== null && !isEmptyAdf(parsed);
	}, [contentBodyValue, contentId, getAdfDocument]);

	const hasContent = getAdfDocument ? !isEditorEmpty : hasContentFromBody;

	// Measures treatment users who see an otherwise usable button disabled by another flow before
	// they click it. They cannot click, so the click exposure never counts them. A click in this
	// mount has already recorded the exposure, so a block after it (such as the user's own flow
	// being admitted) is not reported, and neither is a block both groups share (no content, the
	// parent disabling it, or the Smart Creation modal). Once per mount; records no exposure.
	const hasClickedRef = useRef(false);
	const hasReportedBlockedButtonRef = useRef(false);
	useEffect(() => {
		if (
			hasReportedBlockedButtonRef.current ||
			hasClickedRef.current ||
			isDisabled ||
			!hasContent ||
			showSmartCreationModal ||
			!isNewStartBlocked
		) {
			return;
		}
		hasReportedBlockedButtonRef.current = true;
		reportLifecycleDiagnostic('observed', {
			operation: 'remixButtonBlocked',
			entryPath: 'popup-open',
		});
	}, [
		hasContent,
		isDisabled,
		isNewStartBlocked,
		reportLifecycleDiagnostic,
		showSmartCreationModal,
	]);

	const resolvedAdfDocument = getAdfDocument ? lazyAdfDocument : viewOpenAdfDocument;

	const loadAdfDocument = useCallback(async () => {
		if (getAdfDocument) {
			return getAdfDocument();
		}
		if (!contentBodyValue) {
			return null;
		}
		return parseObjectSidebarRemixAdf(contentBodyValue, contentId);
	}, [getAdfDocument, contentBodyValue, contentId]);

	const loadLifecycleTreatmentAdfDocument = useCallback(async () => {
		const adfSourceVersion = adfSourceVersionRef.current;
		const {
			contentId: committedContentId,
			contentBodyValue: committedContentBodyValue,
			getAdfDocument: currentGetAdfDocument,
		} = lifecycleAdfSourceRef.current;
		const adfDocument = currentGetAdfDocument
			? await currentGetAdfDocument()
			: committedContentBodyValue
				? parseObjectSidebarRemixAdf(committedContentBodyValue, committedContentId)
				: null;
		return adfSourceVersionRef.current === adfSourceVersion ? adfDocument : null;
	}, []);

	const [isLoadingChunk, setIsLoadingChunk] = useState(false);
	// Tracks whether the popup has ever been activated so the lazy chunk is loaded
	const [hasBeenActivated, setHasBeenActivated] = useState(false);

	// Preload the heavy popup chunk on hover/focus so it's ready by the time the user clicks
	const handlePreload = useCallback(() => {
		void remixObjectPagePopupLoader();
	}, []);

	const clearRemixUiState = useCallback(() => {
		setIsModalOpen(false);
		setViewOpenAdfDocument(null);
		setLazyAdfDocument(null);
		isRemixStartActive.current = false;
	}, []);
	const abortActiveRemixStartRef = useRef<
		(reason: string, options?: { releaseLifecycle?: boolean }) => void
	>(() => {});
	const dismissRemixPicker = useCallback(() => {
		const reason = 'User closed remix overlay';
		abortActiveRemixStartRef.current(reason);
		abortRemixCreateExperience(experienceTracker, reason);
		clearRemixUiState();
	}, [clearRemixUiState, experienceTracker]);

	const closeSessionModal = useCallback(
		(options?: PageRemixCloseOptions) => {
			closeObjectSidebarRemixModal(options);
			if (options?.remixLifecycleEndReason === 'handed-off') {
				clearRemixUiState();
				return;
			}
			dismissRemixPicker();
		},
		[clearRemixUiState, closeObjectSidebarRemixModal, dismissRemixPicker],
	);
	useEffect(() => {
		if (
			!contentId ||
			!isModalOpen ||
			isOwnFlowAdmitted ||
			!UNSAFE_expValEqualsNoExposure(
				'cc-ce-maui-close-object-sidebar-on-selection-remix',
				'isEnabled',
				true,
			)
		) {
			return;
		}
		return subscribeToObjectSidebarRemixPickerClose((requestedContentId: string) => {
			if (requestedContentId === contentId) {
				dismissRemixPicker();
			}
		});
	}, [contentId, dismissRemixPicker, isModalOpen, isOwnFlowAdmitted]);

	const fireRemixButtonAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				source: 'remixModalFromObjectSidebar',
				attributes: {
					entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				},
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleClick = useCallback(async () => {
		if (!hasContent || showSmartCreationModal) {
			return;
		}

		// Both lifecycle groups reach this point, so read the cohort here, which records the
		// exposure, before the treatment-only block below. A blocked treatment button is disabled and
		// never gets here, so that case is measured by the `remixButtonBlocked` diagnostic instead.
		const isLifecycleExperimentActive =
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('remix_state_rearchitecture_enabled') &&
			isExperimentEnabled('remix_state_rearchitecture_object_sidebar');
		hasClickedRef.current = true;
		if (isNewStartBlocked) {
			return;
		}
		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		const isAnyPageRemixModalOpen = isObjectSidebarRemixModalOpen || isModalOpen;

		if (shouldAttemptLifecycleAdmission) {
			if (isAnyPageRemixModalOpen || isLoadingChunk) {
				if (isObjectSidebarRemixModalOpen) {
					closeSessionModal();
				} else {
					dismissRemixPicker();
				}
				return;
			}
		}

		if (!shouldAttemptLifecycleAdmission && (isAnyPageRemixModalOpen || isLoadingChunk)) {
			dismissRemixPicker();
			return;
		}

		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				remixSource: OBJECT_LEVEL_REMIX_SOURCE,
				product: 'confluence',
			},
		});
		startRemixCreateExperience({
			experienceTracker,
			attributes: {
				entryPoint: OBJECT_LEVEL_REMIX_ENTRY_POINT,
				remixSource: OBJECT_LEVEL_REMIX_SOURCE,
				product: 'confluence',
			},
		});
		isRemixStartActive.current = true;
		isLifecycleTreatmentActiveRef.current = isLifecycleExperimentActive;
		if (isLifecycleExperimentActive) {
			lifecycleAdfSourceRef.current = { contentId, contentBodyValue, getAdfDocument };
			adfSourceVersionRef.current += 1;
		}
		const startRequestId = isLifecycleExperimentActive ? startRequestIdRef.current + 1 : undefined;
		if (startRequestId !== undefined) {
			startRequestIdRef.current = startRequestId;
		}
		const adfSourceVersion = isLifecycleExperimentActive ? adfSourceVersionRef.current : undefined;
		const isCurrentStart = () =>
			startRequestId !== undefined &&
			adfSourceVersion !== undefined &&
			startRequestIdRef.current === startRequestId &&
			adfSourceVersionRef.current === adfSourceVersion;
		// Ensure the popup chunk is loaded before opening — the hover preload
		// means this will typically resolve instantly from cache
		setIsLoadingChunk(true);
		await remixObjectPagePopupLoader();
		setIsLoadingChunk(false);

		if (!isLifecycleExperimentActive) {
			if (shouldAttemptLifecycleAdmission) {
				const doc = await loadAdfDocument();
				if (!doc || isEmptyAdf(doc)) {
					return;
				}

				const lifecycleAdmission = admitObjectSidebarRemixLifecycle();
				if (lifecycleAdmission === 'rejected') {
					return;
				}

				hasOpenedRemixModalRef.current = true;
				if (getAdfDocument) {
					setLazyAdfDocument(doc);
				} else {
					setViewOpenAdfDocument(doc);
				}
				setIsModalOpen(true);
				setHasBeenActivated(true);
			} else if (getAdfDocument) {
				const doc = await getAdfDocument();
				if (doc) {
					setLazyAdfDocument(doc);
					hasOpenedRemixModalRef.current = true;
					setIsModalOpen(true);
					setHasBeenActivated(true);
				}
			} else if (contentBodyValue) {
				const doc = parseObjectSidebarRemixAdf(contentBodyValue, contentId);
				if (doc) {
					setViewOpenAdfDocument(doc);
					hasOpenedRemixModalRef.current = true;
					setIsModalOpen(true);
					setHasBeenActivated(true);
				}
			}
			fireRemixButtonAnalytics();
			return;
		}

		if (!isCurrentStart()) {
			return;
		}

		const startResult = await prepareObjectSidebarPageRemixStart({
			resolveAdfDocument: loadLifecycleTreatmentAdfDocument,
			isCurrent: isCurrentStart,
			onRejected: (reason) => {
				reportLifecycleDiagnostic('rejected', {
					operation: 'preparePageRemixStart',
					entryPath: 'popup-open',
					rejectionReason: reason,
				});
			},
		});

		if (startResult.status === 'rejected') {
			return;
		}

		hasOpenedRemixModalRef.current = true;
		openObjectSidebarRemixModal(startResult.adfDocument, loadLifecycleTreatmentAdfDocument, {
			isLifecycleTreatment: isLifecycleExperimentActive,
		});
		fireRemixButtonAnalytics();
	}, [
		experienceTracker,
		hasContent,
		isModalOpen,
		isObjectSidebarRemixModalOpen,
		isNewStartBlocked,
		showSmartCreationModal,
		dismissRemixPicker,
		closeSessionModal,
		isLoadingChunk,
		getAdfDocument,
		contentBodyValue,
		loadAdfDocument,
		contentId,
		loadLifecycleTreatmentAdfDocument,
		fireRemixButtonAnalytics,
		shouldAttemptLifecycleAdmission,
		admitObjectSidebarRemixLifecycle,
		openObjectSidebarRemixModal,
		reportLifecycleDiagnostic,
	]);

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	const abortActiveRemixStart = useCallback(
		(reason: string, options: { releaseLifecycle?: boolean } = {}) => {
			if (!isRemixStartActive.current) {
				return;
			}
			if (options.releaseLifecycle !== false) {
				releaseRetainedLifecycleForToken(retainedLifecycleToken, 'abandoned');
			}
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason,
				attributes: { reason },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasOpenedRemixModalRef.current,
			);
			isRemixStartActive.current = false;
		},
		[releaseRetainedLifecycleForToken, retainedLifecycleToken],
	);
	abortActiveRemixStartRef.current = abortActiveRemixStart;
	useEffect(() => {
		return () =>
			abortActiveRemixStartRef.current('User closed remix overlay', {
				releaseLifecycle: false,
			});
	}, []);

	const onLocalModalStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		if (options?.reopenModal) {
			hasOpenedRemixModalRef.current = true;
			setIsModalOpen(true);
		}
	}, []);

	const remixPageLabelMessage = i18n.remixPageLabel;

	const hasContentTooltipMessage = i18n.remixPageTooltip;

	const inProgressTooltipMessage = i18n.remixInProgressTooltip;

	const emptyTooltipMessage = i18n.remixPageEmptyTooltip;

	const shouldUseLifecycleCloseHandler = retainedLifecycleToken !== undefined;

	const isRemixInProgress = showSmartCreationModal || isNewStartBlocked;

	let tooltipContent: string | undefined;
	if (isRemixInProgress) {
		tooltipContent = formatMessage(inProgressTooltipMessage);
	} else if (isObjectSidebarRemixModalOpen || isModalOpen) {
		tooltipContent = undefined;
	} else if (hasContent) {
		tooltipContent = formatMessage(hasContentTooltipMessage);
	} else {
		tooltipContent = formatMessage(emptyTooltipMessage);
	}

	return (
		<div
			ref={registerPopupAnchor}
			css={remixButtonContainerStyle}
			data-testid="sidebar-remix-button-container"
			onMouseEnter={handlePreload}
			onFocus={handlePreload}
		>
			<Tooltip position="left" content={tooltipContent} hideTooltipOnClick>
				<IconButton
					onClick={handleClick}
					appearance="subtle"
					shape={isRovoButtonEnabled ? 'default' : 'circle'}
					icon={AiGenerativeRemixIcon}
					label={formatMessage(remixPageLabelMessage)}
					testId="sidebar-remix-button"
					isLoading={isLoadingChunk}
					isDisabled={isDisabled || !hasContent || isRemixInProgress}
					// The session popup has no overlay trigger to show the selected state.
					isSelected={
						isObjectSidebarRemixModalOpen && !showSmartCreationModal && !isNewStartBlocked
					}
				/>
			</Tooltip>
			{hasBeenActivated && (
				<RemixObjectPagePopup
					adfDocument={resolvedAdfDocument ?? ({ type: 'doc', version: 1, content: [] } as DocNode)}
					isModalOpen={isModalOpen}
					isRemixStartBlocked={isRemixStartBlockedByActiveFlow}
					onClose={clearRemixUiState}
					onStopThinking={onLocalModalStopThinking}
					popupRepositionKey={rightSidebarWidth}
					onOpenSidebarConversation={handleOpenSidebarConversation}
					shouldWaitForRemixHandoffBeforeClose={shouldUseLifecycleCloseHandler}
					prefetchedSpaceHiddenOptions={
						didPrefetchSpaceOptions ? prefetchedSpaceHiddenOptions : undefined
					}
					trigger={(triggerProps) => (
						<div css={popupTriggerOverlayStyle}>
							<IconButton
								{...triggerProps}
								css={placeholderButtonStyle}
								appearance="subtle"
								shape={isRovoButtonEnabled ? 'default' : 'circle'}
								icon={AiGenerativeRemixIcon}
								label={formatMessage(remixPageLabelMessage)}
								testId="sidebar-remix-button"
								isDisabled={isDisabled || !hasContent || isRemixInProgress}
								isSelected={isModalOpen && !showSmartCreationModal}
								aria-hidden="true"
							/>
						</div>
					)}
				/>
			)}
		</div>
	);
};
